// Amiri Font for jsPDF - Arabic Support Implementation
// 
// This file contains functions to load and use the Amiri Regular font with jsPDF
// Font: Amiri Regular (أميري) - Classical Arabic Naskh typeface
// Source: https://github.com/aliftype/amiri
// License: SIL Open Font License (OFL)
//
// Usage:
// import { addAmiriFont, addArabicText } from './utils/amiri-font.js';
// const doc = new jsPDF();
// await addAmiriFont(doc);
// doc.setFont('Amiri-Regular', 'normal');
// doc.text('النص العربي', 20, 20);

// Removed unused jsPDF import

/**
 * Load font data from a URL or file path
 * @param {string} fontPath - Path to the font file
 * @returns {Promise<string>} Base64 encoded font data
 */
async function loadFontData(fontPath) {
  try {
    const response = await fetch(fontPath);
    if (!response.ok) {
      throw new Error(`Failed to load font: ${response.statusText}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // Convert to base64
    let binary = '';
    for (let i = 0; i < uint8Array.byteLength; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }
    
    return btoa(binary);
  } catch (error) {
    console.error('Error loading font data:', error);
    throw error;
  }
}

/**
 * Add Amiri font to a jsPDF document
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {string} fontPath - Optional custom path to the font file
 * @returns {Promise<boolean>} Success status
 */
export const addAmiriFont = async (doc, fontPath = '/fonts/Amiri-Regular.ttf') => {
  try {
    console.log('Loading Amiri font...');
    
    // Load the font data
    const fontData = await loadFontData(fontPath);
    
    // Add the font to jsPDF virtual file system
    doc.addFileToVFS('Amiri-Regular.ttf', fontData);
    
    // Register the font with jsPDF
    doc.addFont('Amiri-Regular.ttf', 'Amiri-Regular', 'normal');
    
    console.log('Amiri font loaded successfully');
    return true;
  } catch (error) {
    console.error('Failed to load Amiri font:', error);
    return false;
  }
};

/**
 * Check if Amiri font is loaded in the document
 * @param {jsPDF} doc - The jsPDF document instance
 * @returns {boolean} Whether the font is loaded
 */
export const isAmiriFontLoaded = (doc) => {
  try {
    const fontList = doc.getFontList();
    return 'Amiri-Regular' in fontList;
  } catch (e) {
    return false;
  }
};

/**
 * Check if text contains Arabic characters
 * @param {string} text - Text to check
 * @returns {boolean} Whether the text contains Arabic characters
 */
export const hasArabicText = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Arabic Unicode ranges:
  // U+0600–U+06FF: Arabic
  // U+0750–U+077F: Arabic Supplement
  // U+08A0–U+08FF: Arabic Extended-A
  // U+FB50–U+FDFF: Arabic Presentation Forms-A
  // U+FE70–U+FEFF: Arabic Presentation Forms-B
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  return arabicRegex.test(text);
};

/**
 * Add text with proper font handling (Arabic or Latin)
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {string} text - Text to add
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {Object} options - Text options
 * @returns {boolean} Success status
 */
export const addText = (doc, text, x, y, options = {}) => {
  const {
    fontSize = 12,
    align = 'left',
    maxWidth = null,
    lineHeight = 1.2,
    color = [0, 0, 0]
  } = options;
  
  try {
    // Set text color
    doc.setTextColor(...color);
    
    // Set font size
    doc.setFontSize(fontSize);
    
    // Check if text contains Arabic characters
    if (hasArabicText(text)) {
      // Use Amiri font for Arabic text
      if (isAmiriFontLoaded(doc)) {
        doc.setFont('Amiri-Regular', 'normal');
        console.log('Using Amiri font for Arabic text:', text);
      } else {
        console.warn('Amiri font not loaded, using default font for Arabic text');
        doc.setFont('helvetica', 'normal');
      }
    } else {
      // Use default font for non-Arabic text
      doc.setFont('helvetica', 'normal');
    }
    
    // Add text to PDF
    if (maxWidth && doc.splitTextToSize) {
      const lines = doc.splitTextToSize(text, maxWidth);
      let currentY = y;
      
      lines.forEach((line) => {
        doc.text(line, x, currentY, { align });
        currentY += fontSize * lineHeight;
      });
    } else {
      doc.text(text, x, y, { align });
    }
    
    return true;
  } catch (error) {
    console.error('Error adding text:', error);
    return false;
  }
};

/**
 * Create RTL layout helper functions
 */
export const createRTLLayout = (pageWidth) => {
  return {
    // Convert LTR X coordinate to RTL
    rtlX: (ltrX) => pageWidth - ltrX,
    
    // Get RTL text alignment
    rtlAlign: (align) => {
      switch (align) {
        case 'left': return 'right';
        case 'right': return 'left';
        case 'center': return 'center';
        default: return 'right';
      }
    },
    
    // Draw RTL table header
    drawRTLTableHeader: (doc, y, headers, isRTL = false) => {
      const startX = isRTL ? pageWidth - 20 : 20;
      const tableWidth = pageWidth - 40;
      const colWidth = tableWidth / headers.length;
      
      // Draw header background
      doc.setFillColor(41, 98, 255);
      doc.rect(20, y, tableWidth, 8, 'F');
      
      // Draw header text with proper font handling
      headers.forEach((header, index) => {
        const x = isRTL ? 
          startX - (colWidth * (index + 0.5)) : 
          startX + (colWidth * (index + 0.5));
        
        // Use addText for proper Arabic font handling
        addText(doc, header, x, y + 6, { 
          fontSize: 9, 
          align: 'center', 
          color: [255, 255, 255] 
        });
      });
      
      return y + 8;
    },
    
    // Draw RTL table row
    drawRTLTableRow: (doc, y, cells, isRTL = false, isEven = false) => {
      const startX = isRTL ? pageWidth - 20 : 20;
      const tableWidth = pageWidth - 40;
      const colWidth = tableWidth / cells.length;
      
      // Alternating row background
      if (isEven) {
        doc.setFillColor(248, 249, 250);
        doc.rect(20, y, tableWidth, 12, 'F');
      }
      
      // Draw cell content with proper font handling
      cells.forEach((cell, index) => {
        const x = isRTL ? 
          startX - (colWidth * (index + 0.5)) : 
          startX + (colWidth * (index + 0.5));
        
        // Use addText for proper Arabic font handling
        addText(doc, cell, x, y + 4, { 
          fontSize: 8, 
          align: 'center', 
          color: [0, 0, 0] 
        });
      });
      
      return y + 12;
    }
  };
};

/**
 * Alias for backward compatibility
 */
export const setArabicFont = (doc, size = 16) => {
  try {
    if (isAmiriFontLoaded(doc)) {
      doc.setFont('Amiri-Regular', 'normal');
      doc.setFontSize(size);
    return true;
    } else {
      console.warn('Amiri font not loaded, using default font');
      doc.setFontSize(size);
      return false;
    }
  } catch (e) {
    console.error('Error setting Arabic font:', e);
    return false;
  }
};

// For backward compatibility
export const addArabicText = addText;

// Export default function for compatibility
export default addAmiriFont; 